import React, { useEffect, useState } from 'react';
import {
    FlatList,
    Pressable,
    VStack,
    Box,
    HStack,
    Checkbox
} from 'native-base';
import { useFormStyles, useShopStyles } from '../../../shared/styles';
import {
    Effects,
    MenuFilter,
    PotencyUnit,
    StrainType
} from '@springbig/shared/dist/services/dutchie/schema-types';
import { Brand, defaultCategories, ProductListPageProps, useBrandsByRetailerIdQuery, useDutchieStore, useI18n } from '@springbig/shared';
import { ProductListProps } from './ProductList';
import { SBDropdown } from '~/shared/SBDropdown';
import { SBRangeSlider } from '~/shared/SBRangeSlider';
import { Caption } from '~/shared/Typography';

export type FilterModalProp = {
    setShowFilters: () => void;
    filters: MenuFilter | null | undefined;
    setFilters: (filters: MenuFilter) => Promise<void>;
};

export const FilterView = ({ setShowFilters, filters, setFilters }: FilterModalProp) => {

    const { section_background } = useShopStyles();
    const [selectedStrainType, setSelectedStrainType] = useState<StrainType | undefined>(undefined);
    const [selectedEffects, setSelectedEffects] = useState<Array<Effects>>([]);
    const [priceMinValue, setPriceMinValue] = useState(filters?.minPrice ?? 0);
    const [priceMaxValue, setPriceMaxValue] = useState(filters?.maxPrice ?? 1000);
    const [thcMinValue, setThcMinValue] = useState(filters?.potencyThc?.min ?? 0);
    const [thcMaxValue, setThcMaxValue] = useState(filters?.potencyThc?.max ?? 100);
    const [cbdMinValue, setCbdMinValue] = useState(filters?.potencyCbd?.min ?? 0);
    const [cbdMaxValue, setCbdMaxValue] = useState(filters?.potencyThc?.max ?? 100);
    const { t } = useI18n();
    const {
        setMenuCategories,
        retailer,
        menuType,
        brandFilter,
        setBrandFilter,
        categoryFilter,
        setCategoryFilter
    } = useDutchieStore();
    const {
        input_checkbox_background,
        input_checkbox_border,
        input_checkbox_icon, 
        input_checkbox_icon_checked_background
    } = useFormStyles();
    const [brands, setBrands] = useState<Brand[]>([]);
    const { mutate: getBrands, data, isLoading } = useBrandsByRetailerIdQuery();

    useEffect(() => {

        if (retailer && menuType && !data && !isLoading) {
            const variables = {
                'retailerId': retailer.id,
                menuType,
            };
            getBrands(variables, {
                onSuccess(data) {
                    if (data?.menu.brands) {
                        setBrands(data?.menu.brands);
                    }
                },
            });
        }
    }, [retailer, menuType, getBrands]);

    useEffect(() => {

        if (filters) {
            if (filters.strainType) {
                setSelectedStrainType(filters.strainType);
            }

            if (filters.effects) {
                setSelectedEffects(filters.effects);
            }
        }
    }, []);

    const onSelectFilter = (): void => {

        setShowFilters();
        const newFilters : MenuFilter = {
            strainType: selectedStrainType,
            effects: selectedEffects,
            minPrice: priceMinValue,
            maxPrice: priceMaxValue,
            potencyCbd: { max: cbdMaxValue, min: cbdMinValue, unit: PotencyUnit.Percentage },
            potencyThc: { max: thcMaxValue, min: thcMinValue, unit: PotencyUnit.Percentage },
            brandIds: brandFilter,
            categories: categoryFilter
        };
        setFilters(newFilters);
    };



    const clearFilters = (): void => {

        setShowFilters();
        setMenuCategories(defaultCategories);
        setFilters(null);
        setBrandFilter(undefined);
    };

    const onSelectEffect = (effects: Effects): void => {

        if (selectedEffects.includes(effects)) {
            setSelectedEffects((prevState) => prevState.filter((e) => e !== effects));
        }
        else {
            setSelectedEffects((prevState) => [effects, ...prevState]);
        }
    };

    const onSelectStrainType = (strainType: StrainType): void => {

        if (strainType === selectedStrainType) {
            setSelectedStrainType(undefined);
        }
        else {
            setSelectedStrainType(strainType);
        }
    };

    const onChangeBrand = (item: Brand) => {

        setBrandFilter((prev) => {
            if (prev?.includes(item.id)) {
                return prev?.filter((id) => id !== item.id);
            } else if (prev) {
                return [item.id, ...prev];
            } 

            return [item.id];
        });
    };

    const onChangeCategory = (item: ProductListProps) => {

        setCategoryFilter((prev) => {
            if (prev?.includes(item.category)) {
                return prev?.filter((category) => category !== item.category);
            } else if (prev) {
                return [item.category, ...prev];
            }

            return [item.category];
        });
    };

    return (
        <VStack justifyContent={'space-between'} flex={1}>
            <VStack space={4}>
                <SBDropdown  title="brand" isV2>
                    <FlatList
                        bg={section_background}
                        h={'85 px'}
                        data={brands}
                        renderItem={({ item }: { item: Brand }) =>

                            <Checkbox
                                isChecked={brandFilter?.includes(item.id)}
                                value={item.id}
                                bg={input_checkbox_background}
                                borderColor={input_checkbox_border}
                                _icon={{ color: input_checkbox_icon }}
                                _checked={{ backgroundColor: input_checkbox_icon_checked_background }}
                                onChange={() => onChangeBrand(item)}
                            >
                                <Caption textAlign={'left'}>
                                    { item.name }
                                </Caption>
                            </Checkbox>
                        }
                    />
                </SBDropdown>
                <SBDropdown  title="category" isV2>
                    <FlatList
                        bg={section_background}
                        h={'85 px'}
                        data={defaultCategories.filter(cat => cat.category)}
                        renderItem={({ item }: { item: ProductListProps }) =>

                            <Checkbox
                                isChecked={categoryFilter?.includes(item.category)}
                                value={item.title}
                                bg={input_checkbox_background}
                                borderColor={input_checkbox_border}
                                _icon={{ color: input_checkbox_icon }}
                                _checked={{ backgroundColor: input_checkbox_icon_checked_background }}
                                onChange={() => onChangeCategory(item)}
                            >
                                <Caption textAlign={'left'}>
                                    { item.title }
                                </Caption>
                            </Checkbox>
                        }
                    />
                </SBDropdown>
                <SBDropdown title="effects" isV2>
                    <FlatList
                        bg={section_background}
                        h={'85 px'}
                        data={Object.values(Effects)}
                        renderItem={({ item }: { item: Effects }) =>

                            <Checkbox
                                isChecked={selectedEffects.includes(item)}
                                value={item.toLowerCase().replace('_', ' ')}
                                bg={input_checkbox_background}
                                borderColor={input_checkbox_border}
                                _icon={{ color: input_checkbox_icon }}
                                onChange={() => onSelectEffect(item)}
                            >
                                <Caption textAlign={'left'}>
                                    { item.toLowerCase().replace('_', ' ') }
                                </Caption>
                            </Checkbox>
                        }
                    />
                </SBDropdown>
                <SBDropdown  title="strain type" isV2>
                    <FlatList
                        bg={section_background}
                        data={[StrainType.Indica, StrainType.Sativa, StrainType.Hybrid]}
                        renderItem={({ item, index }: {item: StrainType, index: number}) =>

                            <Checkbox
                                isChecked={selectedStrainType === item}
                                value={item.toLowerCase().replace('_', ' ')}
                                bg={input_checkbox_background}
                                borderColor={input_checkbox_border}
                                _icon={{ color: input_checkbox_icon }}
                                onChange={() => onSelectStrainType(item)}
                            >
                                <Caption textAlign={'left'}>
                                    { item.toLowerCase() }
                                </Caption>
                            </Checkbox>
                        }
                    />
                </SBDropdown>
                <SBDropdown  title="potency" isV2>
                    <Caption px={2}>thc</Caption>
                    <Box bg={section_background} p={1}>
                        <SBRangeSlider
                            minValue={thcMinValue}
                            maxValue={thcMaxValue}
                            lowerBound={0}
                            upperBound={100}
                            onChangeMinValue={(value) => setThcMinValue(value)}
                            onChangeMaxValue={(value) => setThcMaxValue(value)}
                            type='percentage'
                        />
                    </Box>
                    <Caption px={2}>cbd</Caption>
                    <Box bg={section_background} p={1}>
                        <SBRangeSlider
                            minValue={cbdMinValue}
                            maxValue={cbdMaxValue}
                            lowerBound={0}
                            upperBound={100}
                            onChangeMinValue={(value) => setCbdMinValue(value)}
                            onChangeMaxValue={(value) => setCbdMaxValue(value)}
                            type='percentage'
                        />
                    </Box>
                </SBDropdown>
                <SBDropdown
                    title="price"
                    defaultShowChildren={true}
                    isV2
                >
                    <Box bg={section_background} p={1}>
                        <SBRangeSlider
                            minValue={priceMinValue}
                            maxValue={priceMaxValue}
                            lowerBound={0}
                            upperBound={1000}
                            onChangeMinValue={(value) => setPriceMinValue(value)}
                            onChangeMaxValue={(value) => setPriceMaxValue(value)}
                        />
                    </Box>
                </SBDropdown>
            </VStack>
            <HStack justifyContent={'space-between'}>
                <Pressable
                    onPress={clearFilters}
                >
                    <Caption>clear all</Caption>
                </Pressable>
                <Pressable
                    bg={section_background}
                    py={1}
                    px={2}
                    borderRadius={4}
                    onPress={onSelectFilter}
                >
                    <Caption>{ t('shop.apply') }</Caption>
                </Pressable>
            </HStack>
        </VStack>
    );
};